import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';

export const Profile = () => {
  const { user, setUser, logout, apiClient } = useAuth();
  const navigate = useNavigate();
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Power User Mode — admin-only toggle persisted in localStorage so the
  // choice survives reloads and is shared across tabs (the `storage`
  // event keeps the in-memory copy in sync when another tab flips it).
  // Admin users unlock destructive / edit-only features (currently:
  // Edit / Add buttons on the MarketBreadth admin table) only while
  // this flag is on, so a casual admin session cannot accidentally
  // mutate a row.
  const isAdmin = !!user?.Roles?.includes('Admin');
  const [powerUserMode, setPowerUserMode] = useState(() => readPowerUserMode());

  useEffect(() => {
    if (!isAdmin && powerUserMode) {
      // Admins demoted while the page is open (e.g. role revoked via
      // another tab) must lose the flag immediately so the Edit
      // buttons disappear at once.
      writePowerUserMode(false);
      setPowerUserMode(false);
    }
  }, [isAdmin, powerUserMode]);

  useEffect(() => {
    const onStorage = (evt) => {
      if (evt.key !== POWER_USER_MODE_KEY) return;
      const next = evt.newValue === 'true';
      if (!isAdmin && next) return; // ignore flips from non-admins
      setPowerUserMode(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isAdmin]);

  const togglePowerUserMode = () => {
    const next = !powerUserMode;
    writePowerUserMode(next);
    setPowerUserMode(next);
  };

  useEffect(() => {
    if (isEditNameOpen) {
      setNameDraft(user?.Name || '');
      setNameError('');
    }
  }, [isEditNameOpen, user]);

  const openEditName = () => {
    if (!user) return;
    setIsEditNameOpen(true);
  };

  const closeEditName = () => {
    if (isSaving) return;
    setIsEditNameOpen(false);
    setNameError('');
  };

  const handleSaveName = async (evt) => {
    evt.preventDefault();

    const trimmedName = nameDraft.trim();
    if (!trimmedName) {
      setNameError('Name is required.');
      return;
    }

    try {
      setIsSaving(true);
      setNameError('');
      const updatedUser = await apiClient.updateCurrentUserName(trimmedName);
      setUser({ ...user, ...updatedUser });
      setIsEditNameOpen(false);
    } catch (err) {
      setNameError(err?.message || 'Failed to update name.');
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteAccount = () => {
    setDeleteConfirmText('');
    setDeleteError('');
    setIsDeleteOpen(true);
  };

  const closeDeleteAccount = () => {
    if (isDeleting) return;
    setIsDeleteOpen(false);
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError('');
      await apiClient.deleteCurrentUser();
      await logout();
      navigate('/');
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <Container className="mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <Card>
            <CardHeader>
              <h4 className="mb-0">Profile</h4>
            </CardHeader>
            <CardBody>
              <div className="mb-4">
                <h5>User Information</h5>
                <div className="mb-2">
                  <strong>Name:</strong> {user?.Name || 'N/A'}
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 ms-2"
                    onClick={openEditName}
                    disabled={!user}
                    aria-label="Edit name"
                    style={{ verticalAlign: 'baseline', lineHeight: 1 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      aria-hidden="true"
                      style={{ display: 'inline-block', verticalAlign: 'baseline' }}
                    >
                      <path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.5 9.5L3 14l.646-2.646 9.5-9.5zM11.207 2 4 9.207V10h.793L12 2.793 11.207 2z" />
                      <path d="M1 13.5V16h2.5l7.379-7.379-2.5-2.5L1 13.5z" />
                    </svg>
                  </Button>
                </div>
                <div className="mb-2">
                  <strong>Email:</strong> {user?.Email}
                </div>
                <div className="mb-2">
                  <strong>Roles:</strong> {user?.Roles?.join(', ') || 'N/A'}
                </div>
              </div>

              <hr />

              <div className="mt-4">
                <h5>Security</h5>
                <Button color="primary" tag={Link} to="/profile/change-password">
                  Change Password
                </Button>
              </div>

              {isAdmin && (
                <>
                  <hr />
                  <div className="mt-4">
                    <h5>Power User Mode</h5>
                    <p className="text-muted small mb-2">
                      Enable destructive / edit-only admin actions (e.g. Edit and Add Row on the
                      Custom Market Data page). The choice is stored locally in this browser only
                      and is off by default.
                    </p>
                    <FormGroup check>
                      <Label check className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={powerUserMode}
                          onChange={togglePowerUserMode}
                          aria-label="Enable Power User Mode"
                        />
                        <span className="ms-2">
                          {powerUserMode ? 'Enabled' : 'Disabled'}
                        </span>
                      </Label>
                    </FormGroup>
                  </div>
                </>
              )}

              <hr />

              <div className="mt-4">
                <h5 className="text-danger">Danger Zone</h5>
                <p className="text-muted small mb-2">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button color="danger" outline onClick={openDeleteAccount} disabled={!user}>
                  Delete My Account
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal isOpen={isEditNameOpen} toggle={closeEditName} centered>
        <ModalHeader toggle={closeEditName}>Edit Name</ModalHeader>
        <Form onSubmit={handleSaveName}>
          <ModalBody>
            {nameError && <Alert color="danger">{nameError}</Alert>}
            <FormGroup>
              <Label for="profileName">Name</Label>
              <Input
                id="profileName"
                value={nameDraft}
                onChange={(evt) => setNameDraft(evt.target.value)}
                disabled={isSaving}
                autoComplete="name"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeEditName} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isSaving || !nameDraft.trim()}
            >
              {isSaving ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={isDeleteOpen} toggle={closeDeleteAccount} centered>
        <ModalHeader toggle={closeDeleteAccount} className="text-danger">
          Delete Account
        </ModalHeader>
        <ModalBody>
          {deleteError && <Alert color="danger">{deleteError}</Alert>}
          <p>
            This will <strong>permanently delete</strong> your account and all your data.
            This action <strong>cannot be undone</strong>.
          </p>
          <FormGroup>
            <Label for="deleteConfirm">
              Type <strong>DELETE</strong> to confirm
            </Label>
            <Input
              id="deleteConfirm"
              value={deleteConfirmText}
              onChange={(evt) => setDeleteConfirmText(evt.target.value)}
              disabled={isDeleting}
              autoComplete="off"
              placeholder="DELETE"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteAccount} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleDeleteAccount}
            disabled={isDeleting || deleteConfirmText !== 'DELETE'}
          >
            {isDeleting ? <Spinner size="sm" /> : 'Delete My Account'}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

// localStorage key for the Power User Mode toggle. Kept at module
// scope so both Profile.js (writer) and AdminCustomMarketData.js
// (reader) agree on the exact same string. Bumping this invalidates
// every browser's saved choice, e.g. when the meaning of the flag changes.
export const POWER_USER_MODE_KEY = 'wavewise.powerUserMode';

// Reads the persisted Power User Mode flag. Treats missing / malformed
// values as `false` so a corrupted entry never leaves the toggle stuck
// on. Only valid for admin sessions — callers should still gate the
// actual UI behind their own role check.
export const readPowerUserMode = () => {
  try {
    return window.localStorage.getItem(POWER_USER_MODE_KEY) === 'true';
  } catch {
    // localStorage can throw in private-browsing modes or when storage
    // is full; treat any failure as "off" rather than blocking the UI.
    return false;
  }
};

// Writes the persisted Power User Mode flag. Swallows storage errors
// so the in-memory state still reflects the user's choice for the
// remainder of the session.
export const writePowerUserMode = (value) => {
  try {
    window.localStorage.setItem(POWER_USER_MODE_KEY, value ? 'true' : 'false');
  } catch {
    // ignore — see readPowerUserMode.
  }
};
